import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from 'src/app/services/translate.service';
import { MatDialog } from '@angular/material/dialog';
import { GridViewService } from 'src/app/services/grid-view.service';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { Subject, Subscription } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { UserViewService } from 'src/app/services/user-view.service';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { GridView } from 'src/app/models/gridView.model';
import { UserViewLookup } from 'src/app/models/userView.model';
import { ToastService } from 'src/app/services/toast.service';
import { AuthService } from 'src/app/services/auth.service';
import { ViewSelectorBase } from '../../../base/view-selector-base';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { GridPermissions } from '../gridPermissions.enum';

@Component({
  standalone: false,
  selector: 'app-table-view-selector',
  templateUrl: '../../../components/view-selector/view-selector.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../../../components/view-selector/view-selector.scss']
})
export class TableViewSelectorComponent extends ViewSelectorBase<UserViewLookup> implements OnInit, OnDestroy, OnChanges {
  @Input() objectId!: string;
  @Input() gridId!: string;
  @Input() override defaultViewId?: string;
  @Input() override selectedViewId?: string;
  @Input() refreshViews$!: Subject<void>;
  @Input() orgLevelId?: string;
  @Input() override canSaveView: boolean = true;
  @Input() override isTemporaryView: boolean = false;
  @Input() fallbackToFirstView: boolean = true;
  @Output() override addView: EventEmitter<GridView | null> = new EventEmitter<GridView | null>();
  @Output() override viewSelected: EventEmitter<{ view: UserViewLookup, firstRender: boolean }> = new EventEmitter<{ view: UserViewLookup, firstRender: boolean }>();
  @Output() override viewsChanged: EventEmitter<UserViewLookup[]> = new EventEmitter<UserViewLookup[]>();
  @Output() viewDeleted: EventEmitter<string> = new EventEmitter<string>();

  override views: UserViewLookup[] = [];
  override selectedView?: UserViewLookup | null = null;

  private refreshSub?: Subscription;
  private readonly destroy$ = new Subject<void>();

  override cannotSaveViewTooltip = 'Save the dashboard first to enable view management';
  readonly permissionObject = RolePermissionObjects.GridView;
  readonly GridPermissions = GridPermissions;

  constructor(private gridViewService: GridViewService,
    private userViewService: UserViewService,
    private dialog: MatDialog,
    protected toastService: ToastService,
    private translateService: TranslateService,
    private authService: AuthService) {
    super();
  }

  protected get currentUserId(): string {
    return this.authService.getCurrentUser()?.userId ?? '';
  }

  ngOnInit(): void {
    if (!this.isTemporaryView) this.fetchViews();
    this.refreshSub = this.refreshViews$.subscribe(() => {
      if (!this.isTemporaryView) this.fetchViews();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const key = nameOf<TableViewSelectorComponent>(x => x.defaultViewId);
    const orgKey = nameOf<TableViewSelectorComponent>(x => x.orgLevelId);
    const selectedKey = nameOf<TableViewSelectorComponent>(x => x.selectedViewId);
    const temporaryKey = nameOf<TableViewSelectorComponent>(x => x.isTemporaryView);

    if (changes[key] && !changes[key].firstChange) this.sortViews();
    if (changes[selectedKey] && !changes[selectedKey].firstChange) this.syncSelectedView();
    const orgChanged = changes[orgKey] && !changes[orgKey].firstChange;
    const temporaryChanged = changes[temporaryKey] && !changes[temporaryKey].firstChange;
    if ((orgChanged || temporaryChanged) && !this.isTemporaryView) this.fetchViews();
  }

  fetchViews(): void {
    this.isLoading = true;
    const filterBy = { objectId: this.objectId, organizationLevelId: this.orgLevelId };
    this.gridViewService.getAll(JSON.stringify(filterBy))
      .pipe(takeUntil(this.destroy$), finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res) => {
          if (res.length === 1 && this.fallbackToFirstView) this.defaultViewId = res[0].id;
          this.views = res.slice();
          this.sortViews();
          this.viewsChanged.emit(this.views);
        },
        error: (err) => this.toastService.showError(err)
      });
  }

  override sortViews(): void {
    if (!this.views) return;
    this.views.sort((a, b) => {
      if (a.id === this.defaultViewId) return -1;
      if (b.id === this.defaultViewId) return 1;
      return 0;
    });
    const targetView = this.views.find(v => v.id === (this.selectedViewId || this.defaultViewId))
      || (this.fallbackToFirstView ? this.views[0] : null);
    this.selectedView = targetView;
    this.applyViewFilter();
  }

  confirmRename(view: UserViewLookup): void {
    if (view.canManage === false) { this.cancelRenaming(); return; }
    const newName = this.renameInput.trim();
    if (!newName || newName === view.name) {
      this.cancelRenaming();
      return;
    }
    this.isLoading = true;
    this.gridViewService.getById(view.id).pipe(
      switchMap((fullView) => this.gridViewService.update({ ...fullView, viewName: newName })),
      takeUntil(this.destroy$),
      finalize(() => (this.isLoading = false))
    ).subscribe({
      next: () => {
        view.name = newName;
        this.applyViewFilter();
        if (this.selectedView?.id === view.id) this.viewSelected.emit({ view, firstRender: false });
        this.toastService.showSuccess('View renamed successfully', true);
        this.cancelRenaming();
      },
      error: () => {
        this.toastService.showError('Failed to rename view');
        this.cancelRenaming();
      }
    });
  }

  makeDefaultView(view: UserViewLookup): void {
    if (view.id === this.defaultViewId) return;
    this.isLoading = true;
    this.userViewService.saveDefaultView({ gridId: this.gridId, defaultGridViewId: view.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: () => {
        this.defaultViewId = view.id;
        this.toastService.showSuccess('Default view set successfully', true);
      },
      error: (err) => {
        this.isLoading = false;
      },
      complete: () => this.isLoading = false
    });
  }

  duplicateView(view: UserViewLookup): void {
    this.isLoading = true;
    this.gridViewService.getById(view.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (fullView: GridView) => {
        const baseName = `${fullView.viewName} - Copy`;
        let copyIndex = 1, newName = baseName;
        const existingNames = this.views.map(v => v.name);
        while (existingNames.includes(newName)) newName = `${baseName} (${++copyIndex})`;
        this.addView.emit({ ...fullView, id: undefined, viewName: newName });
      },
      error: (err) => {
        this.isLoading = false;
      },
      complete: () => this.isLoading = false
    });
  }

  deleteView(view: UserViewLookup): void {
    if (view.canManage === false) return;
    if (!this.canSaveView) {
      this.toastService.showError(this.translateService.getDynamicTranslation(this.cannotSaveViewTooltip));
      return;
    }
    if (view.id === this.defaultViewId) return;
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: { title: this.translateService.getDynamicTranslation('View'), service: this.gridViewService, customDelete: () => this.gridViewService.delete(view.id) }
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.views = this.views.filter(v => v.id !== view.id);
        this.applyViewFilter();
        this.viewDeleted.emit(view.id);
        if (this.selectedView?.id === view.id) {
          const fallback = this.views.find(v => v.id === this.defaultViewId) || this.views[0];
          if (fallback) this.selectView(fallback, true);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}

